import React from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Chip, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import DepartmentMultiSelect from './DepartmentMultiSelect';
import LabelTemplateEditor from './LabelTemplateEditor';
import {
  LabelMarker,
  LabelTemplateSlug,
  TemplateDraft,
  WarehouseLabelDepartment,
  WarehouseLabelTemplate,
} from '../model';

interface LabelTemplateAccordionProps {
  template: WarehouseLabelTemplate;
  draft: TemplateDraft;
  departments: WarehouseLabelDepartment[];
  codeOwner: Record<string, LabelTemplateSlug>;
  templateNames: Record<string, string>;
  markers: LabelMarker[];
  expanded: boolean;
  isDirty: boolean;
  isSaving: boolean;
  onToggle: () => void;
  onBodyChange: (value: string, source: string) => void;
  onDeptCodesChange: (codes: string[]) => void;
  onSave: () => void;
}

const LabelTemplateAccordion: React.FC<LabelTemplateAccordionProps> = ({
  template,
  draft,
  departments,
  codeOwner,
  templateNames,
  markers,
  expanded,
  isDirty,
  isSaving,
  onToggle,
  onBodyChange,
  onDeptCodesChange,
  onSave,
}) => (
  <Accordion
    expanded={expanded}
    onChange={onToggle}
    variant="outlined"
    disableGutters
    TransitionProps={{ unmountOnExit: true }}
  >
    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Typography variant="h5" fontWeight="bold">
          {template.name}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {(draft.deptCodes ?? []).length} {(draft.deptCodes ?? []).length === 1 ? 'department' : 'departments'}
        </Typography>
        {/* On the summary, not only inside the panel: a collapsed accordion would otherwise
            hide the fact that this template has edits waiting to be saved. */}
        {isDirty && <Chip label="Unsaved changes" color="warning" size="small" />}
      </Box>
    </AccordionSummary>

    <AccordionDetails>
      <DepartmentMultiSelect
        departments={departments}
        slug={template.slug}
        codeOwner={codeOwner}
        templateNames={templateNames}
        value={draft.deptCodes}
        onChange={onDeptCodesChange}
        disabled={isSaving}
      />

      <LabelTemplateEditor body={draft.body} markers={markers} onBodyChange={onBodyChange} />

      {isDirty && (
        <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
          You&apos;ve changed this template. Click Save Template to apply.
        </Alert>
      )}

      <Button variant="contained" color="primary" onClick={onSave} disabled={isSaving || !isDirty} sx={{ mt: 2 }}>
        Save Template
      </Button>
    </AccordionDetails>
  </Accordion>
);

export default LabelTemplateAccordion;
